import {Disposable, Observable, obsArray, subscribe} from 'grainjs';
import {Rpc} from 'grain-rpc';
import {IGameServer, IPlayer, IRoom} from '../common/common';
import {reportError} from './util';

// tslint:disable:no-console

export class PlayerModel extends Disposable {
  public readonly name = Observable.create<string>(this, localStorage.getItem('playerName') || "");
  public readonly lastPlayerRoom = Observable.create<string>(this, localStorage.getItem('lastPlayerRoom') || "");
  public readonly lastPlayerId = Observable.create<string>(this, localStorage.getItem('lastPlayerId') || "");

  constructor() {
    super();
    this.autoDispose(subscribe(this.name, (use, val) => localStorage.setItem('playerName', val)));
    this.autoDispose(subscribe(this.lastPlayerRoom, (use, val) => localStorage.setItem('lastPlayerRoom', val)));
    this.autoDispose(subscribe(this.lastPlayerId, (use, val) => localStorage.setItem('lastPlayerId', val)));
  }
}

interface IPlayerInfo {
  id: string;
  name: string;
}

function applyMove(board: string[], row: number, col: number, mark: string): string[] {
  return board.map((boardRow, i) =>
    (row === i ? boardRow.slice(0, col) + mark + boardRow.slice(col + 1) : boardRow))
}

export type Victory = [[number, number], [number, number]];

export class GameModel extends Disposable {
  public state = Observable.create<'error'|'waiting'|'playing'>(this, 'waiting');
  public errorMessage = Observable.create(this, "");
  public players = this.autoDispose(obsArray<IPlayerInfo>());
  public whoseTurn = Observable.create<number|null>(this, null);
  public board = Observable.create<string[]>(this, ["   ", "   ", "   "]);
  public winner = Observable.create<number|null>(this, null);
  public victory = Observable.create<Victory|null>(this, null);
  private _myIndex: number;
  private _roomWS: WebSocket;

  private _gameServer?: IGameServer;
  private _roomStub?: IRoom;
  private _playerId?: string;
  private _playerStub?: IPlayer;

  constructor(public readonly roomCode: string, public player: PlayerModel) {
    super();
    this.onDispose(() => this._roomWS && this._roomWS.close());
    this._connect();
  }

  public get playerId() { return this._playerId; }
  public getMyIndex() { return this._myIndex; }

  public move(row: number, col: number) {
    return this._playerStub!.move(row, col);
  }

  public newGame() {
    return this._roomStub!.newGame();
  }

  public onClick(row: number, col: number) {
    if (this.whoseTurn.get() !== null && this.whoseTurn.get() !== this._myIndex) {
      console.log("Not our turn", this._myIndex, this.whoseTurn.get());
      return;
    }
    if (this.board.get()[row][col] !== " ") {
      console.log("Not a blank square");
      return;
    }
    this.move(row, col).catch(reportError);
  }

  public reportError(err: Error) {
    reportError(err);
    this.errorMessage.set(err.message);
    this.state.set('error');
  }

  private _connect() {
    const url = new URL("/", location.href);
    url.protocol = (url.protocol === 'https:') ? 'wss:' : 'ws:';
    const ws = this._roomWS = new WebSocket(url.href);
    ws.addEventListener('open', () => this._onConnect(ws).catch(this.reportError.bind(this)));
    ws.addEventListener('error', (ev: Event) => console.warn('WebSocket error:', ev));
  }

  private async _onConnect(ws: WebSocket) {
    console.log("Websocket connected");
    const rpc = new Rpc({sendMessage: (m) => ws.send(JSON.stringify(m))});
    ws.addEventListener('message', (ev) => rpc.receiveMessage(JSON.parse(ev.data)));
    ws.addEventListener('close', () => {
      this._gameServer = undefined;
      this._roomStub = undefined;
      this._playerStub = undefined;
    });
    this._gameServer = rpc.getStub<IGameServer>("server");
    const roomRpcName = await this._gameServer.getRoomRpc(this.roomCode);
    this._roomStub = rpc.getStub<IRoom>(roomRpcName);
    const lastPlayerId = (this.player.lastPlayerRoom.get() === this.roomCode) &&
      this.player.lastPlayerId.get() || undefined;

    this._playerId = await this._roomStub.join(this.player.name.get(), lastPlayerId);
    this.player.lastPlayerRoom.set(this.roomCode);
    this.player.lastPlayerId.set(this._playerId);
    this._playerStub = rpc.getStub<IPlayer>(`player:${this._playerId}`);
    rpc.on('message', (m) => {
      const msg = JSON.parse(m);
      switch (msg.type) {
        case 'players': {
          console.log("Players are:", JSON.stringify(msg.players));
          this.players.set(msg.players);
          this._myIndex = this.players.get().findIndex((p) => (p.id === this._playerId));
          break;
        }
        case 'start': {
          console.log("On start");
          this.whoseTurn.set(null);
          this.board.set(["   ", "   ", "   "]);
          this.winner.set(null);
          this.victory.set(null);
          this.state.set('playing');
          break;
        }
        case 'move': {
          console.log("On move", msg);
          if (this.whoseTurn.get() === null) {
            this.whoseTurn.set(this.players.get().findIndex((p) => (p.id === msg.playerId)));
          }
          const mark = "XO"[this.whoseTurn.get()!];
          this.board.set(applyMove(this.board.get(), msg.row, msg.col, mark));
          const victory = checkForWin(this.board.get(), mark);
          if (victory) {
            this.winner.set(this.whoseTurn.get());
            this.victory.set(victory);
            this.whoseTurn.set(null);
          } else if (checkForTie(this.board.get())) {
            this.winner.set(-1);
            this.whoseTurn.set(null);
          } else {
            this.whoseTurn.set(1 - this.whoseTurn.get()!);
          }
          break;
        }
        default: {
          console.log("Unknown message", msg);
          break;
        }
      }
    });
  }

  public _onMove(moveInfoJson: string) {
    const moveInfo: {index: number, row: number, col: number} = JSON.parse(moveInfoJson);
    console.log("On move:", moveInfo);
  }
}

function checkForWin(board: string[], mark: string): Victory|null {
  for (const row of [0, 1, 2]) {
    if (Array.from(board[row]).every(m => m === mark)) {
      return [[row, 0], [row, 2]];
    }
  }
  for (const col of [0, 1, 2]) {
    if (board.every((row) => row[col] === mark)) {
      return [[0, col], [2, col]];
    }
  }
  if (board.every((row, i) => row[i] === mark)) {
    return [[0, 0], [2, 2]];
  }
  if (board.every((row, i) => row[2 - i] === mark)) {
    return [[0, 2], [2, 0]];
  }
  return null;
}

function checkForTie(board: string[]) {
  return board.every((row) => !row.includes(" "));
}
